import '@xyflow/react/dist/style.css';
import { createFileRoute } from '@tanstack/react-router';
import { CancelledError } from '@tanstack/react-query';
import { ensureProcessDraft } from '@/api/mutations/useProcessDraft/lifecycle';
import { processQueryOptions } from '@/api/queries/useProcesses';
import { processLayoutQueryOptions, seedLayout } from '@/api/queries/useProcessLayout';
import { orgMembersForLookupQueryOptions } from '@/api/queries/useOrgMembers';
import { orgFunctionsForLookupQueryOptions } from '@/api/queries/useOrgFunctions';
import { systemsForLookupQueryOptions } from '@/api/queries/useSystems';
import { externalContactsForLookupQueryOptions } from '@/api/queries/useExternalContacts';
import { requireEditableProcess } from '@/lib/process-route-guards';
import { DraftEditorPage } from '@/pages/processes/editor/draft/DraftEditorPage';
import { PendingProcessLoader } from '@/pages/processes/components/endless-process/PendingProcessLoader';
import { waitForPendingLoader } from '@/pages/processes/components/endless-process/finishGate';
import { BackButton } from '@/components/back-button';

export const Route = createFileRoute('/$slug/processes/editor/$processId')({
  beforeLoad: async ({ params, context }) => {
    await requireEditableProcess(context.queryClient, params);
  },
  loader: async ({ params, context, abortController }) => {
    const { processId } = params;
    const { queryClient } = context;

    // Warm the assignee lookup (swimlanes resolve every lane/chip through
    // it) in parallel with the draft fetches. Fire-and-forget: the editor
    // gates its canvas on lookup readiness, so the loader doesn't need to
    // block on the slowest list, and prefetchQuery swallows errors.
    void queryClient.prefetchQuery(orgMembersForLookupQueryOptions());
    void queryClient.prefetchQuery(orgFunctionsForLookupQueryOptions());
    void queryClient.prefetchQuery(systemsForLookupQueryOptions());
    void queryClient.prefetchQuery(externalContactsForLookupQueryOptions());

    const run = async () => {
      const [, draftResult] = await Promise.all([
        queryClient.ensureQueryData(processQueryOptions(processId)),
        ensureProcessDraft(queryClient, processId)
      ]);
      // Layout must be seeded after `ensureProcessDraft`: a parallel GET can race the
      // POST's backend-seed and cache `{ updatedAt: null }`, and the force-
      // refetch that follows collides with the pending parallel fetch.
      if (draftResult.created) {
        await seedLayout(queryClient, processId, 'draft');
      } else {
        await queryClient.ensureQueryData(processLayoutQueryOptions(processId, 'draft'));
      }
    };

    try {
      await run();
    } catch (err) {
      if (!(err instanceof CancelledError)) throw err;
      // A StrictMode mount→unmount→remount cycle (or a sibling discard
      // refetch) cancelled the observer that our `ensureQueryData` /
      // `fetchQuery` was sharing. Yield so the canceling pass settles, then
      // try once more. Without retry the cache stays empty and the editor
      // hangs on its `!layoutData?.updatedAt` gate.
      await Promise.resolve();
      await run();
    }
    await waitForPendingLoader(processId, abortController.signal);
  },
  pendingComponent: PendingComponent,
  pendingMs: 0,
  pendingMinMs: 0,
  // The loader POSTs a draft, so a hover preload must never run it.
  preload: false,
  component: RouteComponent
});

function PendingComponent() {
  const { slug, processId } = Route.useParams();
  return (
    <div className="flex h-full w-full flex-col">
      <div className="p-2">
        <BackButton to="/$slug/processes/all-processes/$processId" params={{ slug, processId }} />
      </div>
      <div className="flex-1">
        <PendingProcessLoader processId={processId} messageKey="processDraft.preparing" />
      </div>
    </div>
  );
}

function RouteComponent() {
  const { processId } = Route.useParams();
  return <DraftEditorPage processId={processId} />;
}
